import React, { useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  color: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(104, 159, 56, 0.2);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    max-width: 100%;
  }
`;

const Input = styled.input`
  padding: 1.2rem 1.5rem;
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 4px rgba(104, 159, 56, 0.1);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 400;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.07);
  }
`;

const TextArea = styled.textarea`
  padding: 1.2rem 1.5rem;
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 4px rgba(104, 159, 56, 0.1);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 400;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.07);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, var(--primary-color) 0%, #5a8e31 100%);
  color: #ffffff;
  padding: 1.2rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 16px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(104, 159, 56, 0.3);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(104, 159, 56, 0.4);
    background: linear-gradient(135deg, #5a8e31 0%, var(--primary-color) 100%);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(104, 159, 56, 0.3);
  }
`;

const ErrorMessage = styled.p`
  color: #ff4d4d; /* Red error message */
  font-size: 0.9rem;
  margin-top: -0.5rem;
  margin-bottom: 0.5rem;
  text-align: left;
`;

const ContactForm = () => {
  const form = useRef();
  const [errors, setErrors] = React.useState({});

  const validateForm = () => {
    const newErrors = {};
    const name = form.current.user_name.value.trim();
    const email = form.current.user_email.value.trim();
    const message = form.current.message.value.trim();

    if (!name) {
      newErrors.user_name = 'Name is required.';
    }

    if (!email) {
      newErrors.user_email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.user_email = 'Please enter a valid email address.';
    }

    if (!message) {
      newErrors.message = 'Message is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  const sendEmail = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    emailjs
      .sendForm(
        'service_dw34l0l',
        'template_mmddwt8',
        form.current,
        'W005DYmegnW1Xy5pd'
      )
      .then(
        (result) => {
          alert('Message sent successfully!');
          form.current.reset();
          setErrors({}); // Clear errors after successful submission
        },
        (error) => {
          console.error(error); // Log error for debugging
          alert('Failed to send message. Please try again.');
        }
      );
  };

  return (
    <FormContainer ref={form} onSubmit={sendEmail} id="contact">
      <Input type="text" name="user_name" placeholder="Your Name" required />
      {errors.user_name && <ErrorMessage>{errors.user_name}</ErrorMessage>}
      <Input type="email" name="user_email" placeholder="Your Email" required />
      {errors.user_email && <ErrorMessage>{errors.user_email}</ErrorMessage>}
      <TextArea name="message" rows="5" placeholder="Your Message" required />
      {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
      <SubmitButton type="submit">Send Message</SubmitButton>
    </FormContainer>
  );
};

export default ContactForm;